/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { getAssetPath } from '../utils/assetPath'

/**
 * SvgSpriteSymbols - Loads SVG sprite and injects it into the DOM
 *
 * This component fetches the SVG sprite file and injects its content into the DOM
 * as a hidden SVG element. This allows other components to reference the sprite
 * symbols using fragment identifiers (e.g., <use href="#icon-name" />).
 *
 * This approach matches the legacy Icon.jsx pattern and avoids CORS issues that
 * occur when using external SVG references like <use href="external.svg#icon">.
 *
 * The sprite is fetched once and cached for the entire application lifetime.
 */
export const SvgSpriteSymbols: React.FC = () => {
  const [spriteContent, setSpriteContent] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadSprite = async () => {
      try {
        const spriteUrl = getAssetPath('assets/images/sprites/rcm_sprite_main_nav_icons.svg')

        // Use XMLHttpRequest to get the responseXML (matching legacy Icon.jsx pattern)
        const xhr = new XMLHttpRequest()
        xhr.open('GET', spriteUrl)

        xhr.onreadystatechange = function () {
          if (xhr.readyState === XMLHttpRequest.DONE) {
            const status = xhr.status
            if (status === 0 || (status >= 200 && status < 400)) {
              // Get the root SVG element and extract its content
              const svgElement = xhr.responseXML?.querySelector('svg')
              if (svgElement) {
                // Preserve all attributes and content
                const svgContent = svgElement.outerHTML
                setSpriteContent(svgContent)
              }
            } else {
              console.error('Failed to load SVG sprite:', status, xhr.statusText)
            }
            setIsLoading(false)
          }
        }

        xhr.send()
      } catch (error) {
        console.error('Error loading SVG sprite:', error)
        setIsLoading(false)
      }
    }

    loadSprite()
  }, [])

  if (isLoading || !spriteContent) {
    return null
  }

  return (
    <div
      style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
      dangerouslySetInnerHTML={{ __html: spriteContent }}
      aria-hidden="true"
    />
  )
}
